import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * The one markdown renderer for task types.
 *
 * Eleven copies of a `MARKDOWN_COMPONENTS` table existed in six mutually
 * inconsistent variants, because the scaffolding skill told authors to paste
 * it. Markdown therefore rendered subtly differently per task type — same
 * source, different heading scale, different code-block colours, different
 * spacing — with nothing to notice the drift.
 *
 * Two props survive that consolidation, and both name a real distinction
 * rather than preserving an accident:
 *
 *   - `tone` — `'document'` (default) renders code on a light background, for
 *     admin-authored task content. `'transcript'` renders it dark, for model
 *     and agent output, where a terminal-like block is the point.
 *   - `scale` — `'compact'` (default) is the in-form scale every task field
 *     uses. `'prose'` is for a full-page document (the task-type instructions
 *     page), where `text-sm` body copy would be a regression.
 *
 * Math is deliberately NOT a prop here: the two strategies in use pull heavy,
 * different dependencies (KaTeX, MathJax). They live in sibling modules that
 * compose this table, so a task type that does not render math never carries
 * either library into its chunk. See `TaskMarkdownKatex` / `TaskMarkdownMathJax`.
 */
export type TaskMarkdownTone = 'document' | 'transcript';
export type TaskMarkdownScale = 'compact' | 'prose';

export interface TaskMarkdownStyle {
  tone?: TaskMarkdownTone;
  scale?: TaskMarkdownScale;
}

const SCALE = {
  compact: {
    h1: 'mb-3 mt-4 text-xl font-semibold text-gray-900 first:mt-0',
    h2: 'mb-2 mt-4 text-lg font-semibold text-gray-900 first:mt-0',
    h3: 'mb-2 mt-3 text-base font-semibold text-gray-900 first:mt-0',
    p: 'mb-3 text-sm leading-6 text-gray-800 last:mb-0',
    list: 'mb-3 space-y-1 pl-6 text-sm text-gray-800 last:mb-0',
    blockquote: 'mb-3 border-l-4 border-gray-300 pl-3 text-sm italic text-gray-700 last:mb-0',
    block: 'mb-3 last:mb-0',
    pre: 'p-3 text-xs',
    hr: 'my-4 border-gray-300',
  },
  prose: {
    h1: 'mb-4 mt-6 text-3xl font-semibold text-gray-900 first:mt-0',
    h2: 'mb-3 mt-6 text-2xl font-semibold text-gray-900 first:mt-0',
    h3: 'mb-2 mt-5 text-xl font-semibold text-gray-900 first:mt-0',
    p: 'mb-4 text-base leading-7 text-gray-800 last:mb-0',
    list: 'mb-4 space-y-1 pl-6 text-base leading-7 text-gray-800 last:mb-0',
    blockquote: 'mb-4 border-l-4 border-gray-300 pl-4 italic text-gray-700 last:mb-0',
    block: 'mb-4 last:mb-0',
    pre: 'p-4 text-sm',
    hr: 'my-6 border-gray-200',
  },
} as const;

const TONE = {
  document: {
    pre: 'overflow-x-auto rounded-md bg-gray-100 text-gray-800',
    code: 'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800',
  },
  transcript: {
    pre: 'overflow-x-auto rounded-md bg-gray-950 text-gray-100',
    code: 'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800',
  },
} as const;

/**
 * The single component table. The math variants spread this and override
 * `pre` / `code`; nothing else may define its own.
 */
export function markdownComponents({
  tone = 'document',
  scale = 'compact',
}: TaskMarkdownStyle = {}): Components {
  const s = SCALE[scale];
  const t = TONE[tone];

  return {
    h1: ({ node, children, ...props }) => <h1 className={s.h1} {...props}>{children}</h1>,
    h2: ({ node, children, ...props }) => <h2 className={s.h2} {...props}>{children}</h2>,
    h3: ({ node, children, ...props }) => <h3 className={s.h3} {...props}>{children}</h3>,
    p: ({ node, ...props }) => <p className={s.p} {...props} />,
    ul: ({ node, ...props }) => <ul className={`list-disc ${s.list}`} {...props} />,
    ol: ({ node, ...props }) => <ol className={`list-decimal ${s.list}`} {...props} />,
    li: ({ node, ...props }) => <li className="leading-6" {...props} />,
    a: ({ node, children, ...props }) => (
      <a
        className="text-primary underline hover:text-primary-hover"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    ),
    strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
    em: ({ node, ...props }) => <em className="italic" {...props} />,
    blockquote: ({ node, ...props }) => <blockquote className={s.blockquote} {...props} />,
    code: ({ node, className, children, ...props }) => {
      // react-markdown routes both fenced blocks and inline spans here; a
      // newline is the only reliable discriminator across its versions.
      if (/\n/.test(String(children ?? ''))) {
        return (
          <pre className={`${s.block} ${t.pre} ${s.pre}`}>
            <code className={className} {...props}>{children}</code>
          </pre>
        );
      }
      return <code className={t.code} {...props}>{children}</code>;
    },
    table: ({ node, ...props }) => (
      <div className={`overflow-x-auto ${s.block}`}>
        <table className="min-w-full border-collapse text-sm text-gray-800" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
    th: ({ node, ...props }) => (
      <th className="border border-gray-300 px-2 py-1 text-left font-semibold" {...props} />
    ),
    td: ({ node, ...props }) => (
      <td className="border border-gray-300 px-2 py-1 align-top" {...props} />
    ),
    hr: ({ node, ...props }) => <hr className={s.hr} {...props} />,
  };
}

export interface TaskMarkdownProps extends TaskMarkdownStyle {
  children: string;
  /** Wrapper class. Callers own their own container spacing. */
  className?: string;
  /** Narrow per-call overrides. Reach for this only when the difference is
   *  behavioural; styling differences belong in `tone` / `scale`. */
  components?: Partial<Components>;
}

export function TaskMarkdown({
  children, className, components, tone, scale,
}: TaskMarkdownProps) {
  const resolved = React.useMemo(
    () => ({ ...markdownComponents({ tone, scale }), ...components }),
    [tone, scale, components],
  );
  const body = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={resolved}>
      {children}
    </ReactMarkdown>
  );
  return className ? <div className={className}>{body}</div> : body;
}
